import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { api } from '../api';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import InboxRow from './InboxRow';
import LivePausedNotice from './LivePausedNotice';
import LoadError from './LoadError';

const CHANNEL_OPTIONS = [
  ['', 'All channels'], ['sms', 'SMS'], ['email', 'Email'], ['call', 'Call'], ['voicemail', 'Voicemail'],
];
const TABS = [['all', 'All'], ['unread', 'Unread'], ['starred', 'Starred']];

// Ticket 142: the owner select needs to tell "no staff" apart from "/crm/staff
// failed" (external seats get a 403) — useStaff() collapses both to [].
function useOwnerOptions() {
  const [state, setState] = useState({ staff: [], available: true });
  useEffect(() => {
    let alive = true;
    api.get('/crm/staff')
      .then(({ data }) => { if (alive) setState({ staff: data?.items || [], available: true }); })
      .catch(() => { if (alive) setState({ staff: [], available: false }); });
    return () => { alive = false; };
  }, []);
  return state;
}

export default function InboxList({
  items, total, unreadContacts, searchTruncated, tab, onTabChange, q, onQChange, channel, onChannelChange,
  owner, onOwnerChange, selectedId, onSelect, loading, error, onRetry, loadingMore, moreFailed, hasMore, onLoadMore, live,
  checkedIds, onCheck, onCheckAll, onBulkMark, bulkBusy, onToggleStar, starBusyIds,
}) {
  const [qText, setQText] = useState(q);
  const { staff, available: staffAvailable } = useOwnerOptions();
  const checkedCount = items.filter((it) => checkedIds.has(it.contact_id)).length;
  const allChecked = items.length > 0 && checkedCount === items.length;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  // 300ms debounce: local text state drives the input, the debounced value
  // flows up (and into the URL / fetch) so every keystroke doesn't refetch.
  useEffect(() => setQText(q), [q]);
  useEffect(() => {
    const t = setTimeout(() => { if (qText !== q) onQChange(qText); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qText]);

  // Callback ref, not an effect: the sentinel unmounts during every page-1
  // reload and comes back as a new node, which an effect keyed on hasMore
  // would never re-observe.
  const observer = useRef(null);
  const sentinelRef = useCallback((node) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node || typeof window === 'undefined' || !window.IntersectionObserver) return;
    observer.current = new window.IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onLoadMoreRef.current?.();
    });
    observer.current.observe(node);
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col h-full min-h-0" data-testid="inbox-list">
      <LivePausedNotice live={live} />
      <div className="p-3 border-b border-slate-100 space-y-2">
        <div className="flex items-center gap-1" role="tablist">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => onTabChange(key)}
              data-testid={`inbox-tab-${key}`}
              className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${tab === key ? 'text-white' : 'text-slate-500 hover:bg-slate-50'}`}
              style={tab === key ? { background: '#070B5F' } : undefined}
            >
              {label}
              {key === 'unread' && unreadContacts > 0 && (
                <span
                  className={`text-[10px] px-1.5 rounded-full ${tab === key ? 'bg-white/20' : 'text-white'}`}
                  style={tab === key ? undefined : { background: '#070B5F' }}
                  data-testid="inbox-unread-badge"
                >
                  {unreadContacts}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={qText}
            onChange={(e) => setQText(e.target.value)}
            placeholder="Search name, phone, email or messages"
            className="h-8 pl-8 text-sm"
            data-testid="inbox-search"
          />
        </div>
        {searchTruncated && q && (
          <p className="text-[11px] text-amber-700" data-testid="inbox-search-truncated">
            Only the newest message threads that match are shown. Add another word to narrow the search.
          </p>
        )}
        <div className="flex items-center gap-2">
          <Select value={channel || 'all'} onValueChange={(v) => onChannelChange(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs flex-1" data-testid="inbox-channel-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CHANNEL_OPTIONS.map(([v, label]) => <SelectItem key={v || 'all'} value={v || 'all'}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
          {staffAvailable && (
            <Select value={owner || 'all'} onValueChange={(v) => onOwnerChange(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 text-xs flex-1" data-testid="inbox-owner-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All owners</SelectItem>
                <SelectItem value="me">Me</SelectItem>
                {staff.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>)}
                <SelectItem value="unassigned">Unassigned</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        {!error && (
          <div className="flex items-center gap-2 text-xs text-slate-500 min-h-[24px]">
            {items.length > 0 && (
              <Checkbox
                checked={allChecked ? true : checkedCount > 0 ? 'indeterminate' : false}
                onCheckedChange={(v) => onCheckAll(v === true)}
                aria-label="Select all loaded conversations"
                data-testid="inbox-check-all"
              />
            )}
            {checkedCount > 0 ? (
              // Ticket 195: mark read / unread only (no bulk assign or archive).
              <span className="flex items-center gap-2" data-testid="inbox-bulk-bar">
                <span data-testid="inbox-bulk-count">{checkedCount} selected</span>
                <button type="button" disabled={bulkBusy} onClick={() => onBulkMark(true)}
                  className="px-2 py-0.5 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  data-testid="inbox-bulk-read">
                  Mark read
                </button>
                <button type="button" disabled={bulkBusy} onClick={() => onBulkMark(false)}
                  className="px-2 py-0.5 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  data-testid="inbox-bulk-unread">
                  Mark unread
                </button>
                {bulkBusy && <Loader2 className="animate-spin" size={12} />}
              </span>
            ) : (
              <span data-testid="inbox-total">{total} conversation{total === 1 ? '' : 's'}</span>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-400">
            <Loader2 className="animate-spin" size={16} />
          </div>
        ) : error ? (
          <LoadError message={error} onRetry={onRetry} testId="inbox-error" />
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="inbox-empty">No conversations yet</div>
        ) : (
          <>
            <ul className="divide-y divide-slate-100">
              {items.map((item) => (
                <InboxRow
                  key={item.contact_id}
                  item={item}
                  selected={selectedId === item.contact_id}
                  onClick={() => onSelect(item.contact_id)}
                  checked={checkedIds.has(item.contact_id)}
                  onCheck={onCheck}
                  onToggleStar={onToggleStar}
                  starBusy={starBusyIds.has(item.contact_id)}
                />
              ))}
            </ul>
            {hasMore && (
              <div className="p-3 text-center" ref={sentinelRef}>
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  data-testid="inbox-load-more"
                  className="text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50"
                >
                  {loadingMore ? <Loader2 className="animate-spin inline" size={14} /> : moreFailed ? 'Retry' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
